/**
 * EQModule — three-band parametric EQ (low-shelf, peaking, high-shelf).
 *
 * Graph:
 *
 *   input → lowShelf → midPeak → highShelf → output
 *
 * Each band is a BiquadFilterNode. The low and high bands are shelf
 * filters (boost or cut everything below / above the corner); the
 * middle is a peaking filter (boost or cut a band centered at its
 * frequency, with Q controlling the band's width). All three are in
 * series — the user gets a classic three-knob-per-band tone shaper.
 *
 * Parameters expose the frequency and gain of every band, plus the
 * mid Q so the user can dial in narrow surgical cuts or broad shelf-
 * like boosts in the mids. Defaults form a flat response so dropping
 * the EQ into a chain is audibly inert until the user touches a knob.
 *
 * Modulation:
 *   - Mid Freq is the most musical sweep target (synth-style auto-wah).
 *   - The three Gain params expose AudioParams for direct mod-matrix
 *     hookup (filter gain rides like a CV-controlled EQ).
 */

import type { AudioEngine } from '../AudioEngine';
import { NumericParameter, type AnyParameter } from '../../params/Parameter';
import type { ModuleState, SynthModule } from './SynthModule';
import { mintId, readParamState, writeParamState } from './util';

export class EQModule implements SynthModule {
  readonly kind = 'signal' as const;
  readonly type = 'eq' as const;
  readonly displayName = 'EQ';
  readonly id: string;
  bypass = false;

  // ── LOW shelf ──────────────────────────────────────────────────────
  readonly lowFreq = new NumericParameter({
    name: 'Lo Freq',
    default: 200,
    min: 20,
    max: 1000,
    curve: 'exp',
    unit: 'Hz',
    smoothingMs: 20,
    formatter: (v) =>
      v >= 1000 ? `${(v / 1000).toFixed(2)}k` : `${v.toFixed(0)}`,
  });
  readonly lowGain = new NumericParameter({
    name: 'Lo Gain',
    default: 0,
    min: -18,
    max: 18,
    curve: 'linear',
    unit: 'dB',
    smoothingMs: 20,
    // Bias randomization a hair toward boost — a flat random patch
    // is boring; a little extra low-end gives the bank some color.
    randomSkew: 1.2,
    formatter: (v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}`,
  });

  // ── MID peak ───────────────────────────────────────────────────────
  readonly midFreq = new NumericParameter({
    name: 'Mid Freq',
    default: 1000,
    min: 100,
    max: 8000,
    curve: 'exp',
    unit: 'Hz',
    smoothingMs: 20,
    formatter: (v) =>
      v >= 1000 ? `${(v / 1000).toFixed(2)}k` : `${v.toFixed(0)}`,
  });
  readonly midGain = new NumericParameter({
    name: 'Mid Gain',
    default: 0,
    min: -18,
    max: 18,
    curve: 'linear',
    unit: 'dB',
    smoothingMs: 20,
    randomSkew: 1,
    formatter: (v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}`,
  });
  readonly midQ = new NumericParameter({
    name: 'Mid Q',
    default: 1,
    min: 0.3,
    max: 8,
    curve: 'exp',
    smoothingMs: 20,
    // Narrow Qs are surgical — random patches sound better with
    // moderate width.
    randomSkew: 0.8,
    formatter: (v) => v.toFixed(2),
  });

  // ── HIGH shelf ─────────────────────────────────────────────────────
  readonly highFreq = new NumericParameter({
    name: 'Hi Freq',
    default: 6000,
    min: 1000,
    max: 16000,
    curve: 'exp',
    unit: 'Hz',
    smoothingMs: 20,
    formatter: (v) =>
      v >= 1000 ? `${(v / 1000).toFixed(2)}k` : `${v.toFixed(0)}`,
  });
  readonly highGain = new NumericParameter({
    name: 'Hi Gain',
    default: 0,
    min: -18,
    max: 18,
    curve: 'linear',
    unit: 'dB',
    smoothingMs: 20,
    // Mild bias toward boost — same logic as Lo Gain. Air on top
    // generally flatters synth voices.
    randomSkew: 1.2,
    formatter: (v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}`,
  });

  readonly parameters: readonly AnyParameter[] = [
    this.lowFreq,
    this.lowGain,
    this.midFreq,
    this.midGain,
    this.midQ,
    this.highFreq,
    this.highGain,
  ];

  private inputNode: GainNode | null = null;
  private outputNode: GainNode | null = null;
  private lowShelf: BiquadFilterNode | null = null;
  private midPeak: BiquadFilterNode | null = null;
  private highShelf: BiquadFilterNode | null = null;
  private started = false;

  constructor(id?: string) {
    this.id = id ?? mintId('eq');
  }

  get input(): AudioNode | null {
    return this.inputNode;
  }
  get output(): AudioNode | null {
    return this.outputNode;
  }

  init(engine: AudioEngine): void {
    if (this.started) return;
    const ctx = engine.context;
    this.inputNode = ctx.createGain();
    this.outputNode = ctx.createGain();

    this.lowShelf = ctx.createBiquadFilter();
    this.lowShelf.type = 'lowshelf';
    this.lowShelf.frequency.value = this.lowFreq.value;
    this.lowShelf.gain.value = this.lowGain.value;

    this.midPeak = ctx.createBiquadFilter();
    this.midPeak.type = 'peaking';
    this.midPeak.frequency.value = this.midFreq.value;
    this.midPeak.gain.value = this.midGain.value;
    this.midPeak.Q.value = this.midQ.value;

    this.highShelf = ctx.createBiquadFilter();
    this.highShelf.type = 'highshelf';
    this.highShelf.frequency.value = this.highFreq.value;
    this.highShelf.gain.value = this.highGain.value;

    // Series chain — each band's output is the next band's input.
    this.inputNode.connect(this.lowShelf);
    this.lowShelf.connect(this.midPeak);
    this.midPeak.connect(this.highShelf);
    this.highShelf.connect(this.outputNode);

    this.lowFreq.bindAudioParam(this.lowShelf.frequency, ctx);
    this.lowGain.bindAudioParam(this.lowShelf.gain, ctx);
    this.midFreq.bindAudioParam(this.midPeak.frequency, ctx);
    this.midGain.bindAudioParam(this.midPeak.gain, ctx);
    this.midQ.bindAudioParam(this.midPeak.Q, ctx);
    this.highFreq.bindAudioParam(this.highShelf.frequency, ctx);
    this.highGain.bindAudioParam(this.highShelf.gain, ctx);

    this.started = true;
  }

  /**
   * Mod-routing lookup. Frequencies are AudioParams on BiquadFilterNode,
   * so audio-rate mod sources (LFO → Mid Freq) get sample-accurate
   * sweeps for free. Gains are also exposed for cross-fade-style
   * automation tricks.
   */
  getAudioParam(paramName: string): AudioParam | null {
    if (paramName === 'Lo Freq') return this.lowShelf?.frequency ?? null;
    if (paramName === 'Lo Gain') return this.lowShelf?.gain ?? null;
    if (paramName === 'Mid Freq') return this.midPeak?.frequency ?? null;
    if (paramName === 'Mid Gain') return this.midPeak?.gain ?? null;
    if (paramName === 'Mid Q') return this.midPeak?.Q ?? null;
    if (paramName === 'Hi Freq') return this.highShelf?.frequency ?? null;
    if (paramName === 'Hi Gain') return this.highShelf?.gain ?? null;
    return null;
  }

  dispose(): void {
    try {
      this.inputNode?.disconnect();
      this.lowShelf?.disconnect();
      this.midPeak?.disconnect();
      this.highShelf?.disconnect();
      this.outputNode?.disconnect();
    } catch {
      /* already disconnected */
    }
    this.started = false;
  }

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    if (state.bypass !== undefined) this.bypass = state.bypass;
    readParamState(this.parameters, state.params, 'preset');
  }
}
